import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import LoadingSpinner from './common/LoadingSpinner';
import Alert from './common/Alert';
import AsistenteService from '../services/AsistenteService';
import ActiveRegionalBadge from './common/ActiveRegionalBadge';
import RegionalService from '../services/RegionalService';
import materiaService from '../services/MateriaService';
import ProcesosMenu from './common/ProcesosMenu';
import './NewAssistant.css';
import {
  isGptClassicEnabled as hasGptClassicFeature,
  isOpenAIAssistantEnabled as hasOpenAIAssistantFeature,
  isRagFlowAssistantEnabled as hasRagFlowAssistantFeature
} from '../utils/adminMenu';
import useRegionalFeatureFlags from '../hooks/useRegionalFeatureFlags';
import ProfessorMenuOptions from './sidebar/ProfessorMenuOptions';
import { navigateProfessorMenuItem, NEW_ASSISTANT_MENU_ITEM, NEW_RAGFLOW_ASSISTANT_MENU_ITEM } from './sidebar/menuConfig';
import { isInstitutionalAssistantType } from '../utils/asistenteTypes';
import {
  buildRagFlowModelLabel,
  getDefaultRagFlowModelId,
  normalizeRagFlowModelsResponse
} from '../utils/ragFlowModels';

const isProfessorUser = (currentUser) => {
  if (!currentUser) return false;
  const roleName = (currentUser.roleName || currentUser.nombreRol || currentUser.role || '').toLowerCase();
  return roleName === 'profesor' || roleName.includes('profesor') || roleName.includes('profe');
};

const MAX_FILE_NAME_LENGTH = 'LED2025-planificacion_logica_y_estructu'.length;

const formatFileName = (name) => {
  if (!name) return '';
  return name.length > MAX_FILE_NAME_LENGTH ? `${name.slice(0, MAX_FILE_NAME_LENGTH)}...` : name;
};

const VALIDATION_ERROR_MESSAGE = 'No pudimos procesar los archivos. Verifica que todos sean PDF válidos y que no superen los límites permitidos.';

const getFriendlyApiErrorMessage = (error, fallback) => {
  if (!error || !error.message) {
    return fallback;
  }
  const normalized = error.message.toLowerCase();
  if (normalized.includes('validation')) {
    return VALIDATION_ERROR_MESSAGE;
  }
  return error.message;
};

const EditAssistant = ({ provider = 'openai' }) => {
  const { assistantId } = useParams();
  const { user, logout, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();
  const isProfesor = isProfessorUser(user);
  const isRagFlowProvider = String(provider).toLowerCase() === 'ragflow';
  const providerLabel = isRagFlowProvider ? 'AVE' : 'OpenAI';
  const { featureFlags } = useRegionalFeatureFlags(user, { enabled: !authLoading });
  const isGptClassicEnabled = hasGptClassicFeature(featureFlags);
  const isOpenAIAssistantEnabled = hasOpenAIAssistantFeature(featureFlags);
  const isRagFlowAssistantEnabled = hasRagFlowAssistantFeature(featureFlags);
  const isCurrentAssistantFeatureEnabled = isRagFlowProvider ? isRagFlowAssistantEnabled : isOpenAIAssistantEnabled;

  const [isLoading, setIsLoading] = useState(true);
  const [assistantData, setAssistantData] = useState(null);
  const [alert, setAlert] = useState({ show: false, message: '', type: 'error', title: '' });
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [formData, setFormData] = useState({
    nombre: '',
    nicknameUrl: '',
    descripcion: '',
    promptExtra: '',
    instruccionesInstitucionales: '',
    idTipoAsistente: '',
    idRegional: '',
    idArea: '',
    idMateria: '',
    publico: true,
    passwordAsistente: ''
  });
  const [materias, setMaterias] = useState([]);
  const [materiasLoading, setMateriasLoading] = useState(false);
  const [materiasError, setMateriasError] = useState('');
  const [modelName, setModelName] = useState('');
  const [ragFlowModels, setRagFlowModels] = useState([]);
  const [ragFlowModelsLoading, setRagFlowModelsLoading] = useState(false);
  const [ragFlowModelsError, setRagFlowModelsError] = useState('');
  const [existingFiles, setExistingFiles] = useState([]);
  const [newFiles, setNewFiles] = useState([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [reloadCounter, setReloadCounter] = useState(0);
  const materiaEsObligatoria = true;
  const isInstitutionalTipoSeleccionado = useMemo(
    () => isInstitutionalAssistantType({ id: formData.idTipoAsistente }),
    [formData.idTipoAsistente]
  );
  const shouldShowPromptSettings = !isRagFlowProvider;
  const shouldRequireInstitutionalInstructions = shouldShowPromptSettings && isInstitutionalTipoSeleccionado;
  const ragFlowModelOptions = useMemo(() => {
    if (!isRagFlowProvider || !modelName || ragFlowModels.some((model) => model.id === modelName)) {
      return ragFlowModels;
    }

    return [
      {
        id: modelName,
        nombre: modelName,
        proveedor: '',
        instancia: '',
        tipo: 'chat',
        status: 'guardado',
        esDefault: false,
        caracteristicas: ['Modelo guardado actualmente en el asistente.']
      },
      ...ragFlowModels
    ];
  }, [isRagFlowProvider, modelName, ragFlowModels]);
  const selectedRagFlowModelConfig = useMemo(() => {
    if (!isRagFlowProvider || !modelName) {
      return null;
    }

    return ragFlowModelOptions.find((model) => model.id === modelName) || null;
  }, [isRagFlowProvider, modelName, ragFlowModelOptions]);

  useEffect(() => {
    if (authLoading || !assistantId) {
      return;
    }

    if (!isProfesor) {
      setIsLoading(false);
      return;
    }

    if (!isCurrentAssistantFeatureEnabled) {
      setAlert({
        show: true,
        title: 'Funcionalidad deshabilitada',
        message: `El feature flag de Asistentes ${providerLabel} no esta habilitado para esta regional.`,
        type: 'warning'
      });
      setIsLoading(false);
      return;
    }

    const fetchAssistant = async () => {
      try {
        setIsLoading(true);
        const response = await AsistenteService.getAsistenteById(assistantId);
        const asistente = response.asistente || null;
        setAssistantData(asistente);
        if (asistente) {
          setFormData({
            nombre: asistente.nombre || '',
            nicknameUrl: asistente.nicknameUrl || asistente.NicknameUrl || '',
            descripcion: asistente.descripcion || '',
            promptExtra: asistente.promptExtra || asistente.PromptExtra || '',
            instruccionesInstitucionales: asistente.instruccionesInstitucionales || asistente.InstruccionesInstitucionales || '',
            idTipoAsistente: asistente.idTipoAsistente || asistente.IdTipoAsistente || '',
            idRegional: asistente.idRegional || asistente.IdRegional || '',
            idArea: asistente.idArea || asistente.IdArea || '',
            idMateria: asistente.idMateria || asistente.IdMateria || '',
            publico: asistente.publico ?? asistente.Publico ?? true,
            passwordAsistente: asistente.passwordAsistente || asistente.PasswordAsistente || ''
          });
        }
        const ragFlowModelo = asistente?.ragFlowModelo || asistente?.RagFlowModelo || response.ragFlowModelo || response.RagFlowModelo || '';
        setModelName(isRagFlowProvider ? (ragFlowModelo || response.modeloOpenAI || '') : (response.modeloOpenAI || ''));
        if (asistente?.archivos && Array.isArray(asistente.archivos)) {
          setExistingFiles(
            asistente.archivos.map(archivo => ({
              fileId: archivo.id,
              fileName: archivo.nombreArchivo,
              fechaAlta: archivo.fechaAlta
            }))
          );
        } else {
          setExistingFiles([]);
        }
        setNewFiles([]);
      } catch (error) {
        console.error('Error al obtener asistente:', error);
        setAlert({
          show: true,
          title: 'Error al obtener el asistente',
          message: getFriendlyApiErrorMessage(error, 'No pudimos obtener los datos del asistente. Inténtalo nuevamente.'),
          type: 'error'
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchAssistant();
  }, [assistantId, authLoading, isProfesor, isCurrentAssistantFeatureEnabled, providerLabel, isRagFlowProvider, reloadCounter]);

  useEffect(() => {
    if (!isProfesor) return;
    const activeRegionalId = formData.idRegional || RegionalService.getActiveRegionalId();
    if (!activeRegionalId) return;

    const fetchMaterias = async () => {
      setMateriasLoading(true);
      setMateriasError('');
      try {
        const list = await materiaService.getMisMaterias({ idRegional: activeRegionalId });
        setMaterias(Array.isArray(list) ? list : []);
      } catch (error) {
        setMateriasError(error.message || 'No pudimos cargar tus materias.');
      } finally {
        setMateriasLoading(false);
      }
    };

    fetchMaterias();
  }, [isProfesor, formData.idRegional]);

  useEffect(() => {
    if (!isRagFlowProvider || !isProfesor) {
      return;
    }

    const activeRegionalId = formData.idRegional || RegionalService.getActiveRegionalId();
    if (!activeRegionalId) {
      return;
    }

    let cancelled = false;
    const fetchRagFlowModels = async () => {
      setRagFlowModelsLoading(true);
      setRagFlowModelsError('');
      try {
        const response = await AsistenteService.getRagFlowModelos(activeRegionalId);
        if (cancelled) {
          return;
        }

        const models = normalizeRagFlowModelsResponse(response);
        setRagFlowModels(models);
        const defaultModelId = getDefaultRagFlowModelId(models);
        setModelName((prev) => {
          if (prev) {
            return prev;
          }

          return defaultModelId || '';
        });
      } catch (error) {
        if (!cancelled) {
          setRagFlowModels([]);
          setRagFlowModelsError(error.message || 'No pudimos cargar los modelos de RagFlow.');
        }
      } finally {
        if (!cancelled) {
          setRagFlowModelsLoading(false);
        }
      }
    };

    fetchRagFlowModels();

    return () => {
      cancelled = true;
    };
  }, [isRagFlowProvider, isProfesor, formData.idRegional]);

  const MAX_FILES = 20;

  const totalFilesCount = existingFiles.length + newFiles.length;

  const handleGoBack = () => {
    navigate(isRagFlowProvider ? '/ragflow-assistants' : '/my-assistants');
  };

  const handleLogout = () => {
    logout();
  };

  const handleProfessorMenuSelect = (item) => {
    navigateProfessorMenuItem(navigate, item.id);
  };

  const handleAddFiles = (files) => {
    if (!files || files.length === 0) {
      return;
    }

    const pdfFiles = files.filter(file => file.type === 'application/pdf');
    if (pdfFiles.length !== files.length) {
      setAlert({
        show: true,
        title: 'Archivos inválidos',
        message: 'Solo se permiten archivos PDF.',
        type: 'warning'
      });
    }

    setNewFiles(prev => {
      const availableSlots = MAX_FILES - (existingFiles.length + prev.length);
      if (availableSlots <= 0) {
        setAlert({
          show: true,
          title: 'Límite de archivos',
          message: 'Se alcanzó el límite máximo de archivos.',
          type: 'warning'
        });
        return prev;
      }

      const filesToAdd = pdfFiles.slice(0, availableSlots);
      if (filesToAdd.length < pdfFiles.length) {
        setAlert({
          show: true,
          title: 'Límite de archivos',
          message: `Solo se pueden agregar ${availableSlots} archivos adicionales.`,
          type: 'warning'
        });
      }

      return [...prev, ...filesToAdd];
    });
  };

  const handleRemoveNewFile = (index) => {
    setNewFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleMateriaChange = (value) => {
    const selected = materias.find((m) => (m.id || m.Id) === value);
    const idArea = selected?.idArea || selected?.IdArea || '';
    const idRegional = selected?.idRegional || selected?.IdRegional || formData.idRegional;
    setFormData(prev => ({
      ...prev,
      idMateria: value,
      idArea: value ? idArea : '',
      idRegional: idRegional || prev.idRegional
    }));
  };

  const handleSaveClick = () => {
    if (!formData.nombre.trim()) {
      setAlert({
        show: true,
        title: 'Nombre requerido',
        message: 'Debes ingresar un nombre paral asistente.',
        type: 'error'
      });
      return;
    }

    if (materiaEsObligatoria && materiasLoading) {
      setAlert({
        show: true,
        title: 'Materias cargando',
        message: 'Esperá a que carguen tus materias para continuar.',
        type: 'warning'
      });
      return;
    }

    if (materiaEsObligatoria && materias.length === 0) {
      setAlert({
        show: true,
        title: 'Sin materias asignadas',
        message: 'No tienes materias asignadas para asociar al asistente.',
        type: 'warning'
      });
      return;
    }

    if (materiaEsObligatoria && !formData.idMateria) {
      setAlert({
        show: true,
        title: 'Materia requerida',
        message: 'Selecciona una materia asignada para el asistente.',
        type: 'warning'
      });
      return;
    }

    if (shouldRequireInstitutionalInstructions && !formData.instruccionesInstitucionales.trim()) {
      setAlert({
        show: true,
        title: 'Instrucciones requeridas',
        message: 'Debes ingresar las instrucciones del asistente institucional.',
        type: 'warning'
      });
      return;
    }

    if (isRagFlowProvider && ragFlowModelsLoading) {
      setAlert({
        show: true,
        title: 'Modelos cargando',
        message: 'Esperá a que carguen los modelos AVE para continuar.',
        type: 'warning'
      });
      return;
    }

    if (isRagFlowProvider && !modelName.trim()) {
      setAlert({
        show: true,
        title: 'Modelo requerido',
        message: 'Selecciona un modelo AVE para actualizar el asistente.',
        type: 'warning'
      });
      return;
    }

    if (!formData.publico && !formData.passwordAsistente.trim()) {
      setAlert({
        show: true,
        title: 'Password requerido',
        message: 'Debes ingresar un password cuando el asistente es privado.',
        type: 'error'
      });
      return;
    }

    if (newFiles.length > 0 && !showConfirmation) {
      setShowConfirmation(true);
      return;
    }

    submitUpdate();
  };

  const submitUpdate = async () => {
    try {
      setIsSubmitting(true);
      setAlert({
        show: true,
        title: 'Actualizando asistente',
        message: 'Actualizando asistente... Este proceso puede demorar unos minutos.',
        type: 'info'
      });

      const formDataToSend = new FormData();
      formDataToSend.append('nombre', formData.nombre.trim());
      formDataToSend.append('descripcion', formData.descripcion || '');
      formDataToSend.append('nicknameUrl', formData.nicknameUrl || '');
      if (shouldShowPromptSettings) {
        formDataToSend.append('promptExtra', formData.promptExtra || '');
      }
      if (shouldShowPromptSettings && isInstitutionalTipoSeleccionado) {
        formDataToSend.append('instruccionesInstitucionales', formData.instruccionesInstitucionales || '');
      }
      if (isRagFlowProvider && modelName.trim()) {
        formDataToSend.append('ragFlowModelo', modelName.trim());
      }
      if (formData.idRegional) {
        formDataToSend.append('idRegional', formData.idRegional);
      }
      if (formData.idArea) {
        formDataToSend.append('idArea', formData.idArea);
      }
      if (formData.idMateria) {
        formDataToSend.append('idMateria', formData.idMateria);
      }
      if (formData.idTipoAsistente) {
        formDataToSend.append('idTipoAsistente', formData.idTipoAsistente);
      }
      formDataToSend.append('publico', String(formData.publico));
      formDataToSend.append('conPassword', String(!formData.publico));
      if (!formData.publico && formData.passwordAsistente.trim()) {
        formDataToSend.append('passwordAsistente', formData.passwordAsistente.trim());
      }
      newFiles.forEach(file => {
        formDataToSend.append('archivosPdf', file);
      });

      await AsistenteService.actualizarAsistente(assistantId, formDataToSend, provider);

      setAlert({
        show: true,
        title: 'Asistente actualizado',
        message: 'Asistente actualizado correctamente.',
        type: 'success'
      });
      setShowConfirmation(false);
      setNewFiles([]);
      setReloadCounter(prev => prev + 1);
    } catch (error) {
      console.error('Error al actualizar asistente:', error);
      setAlert({
        show: true,
        title: 'Error al actualizar asistente',
        message: getFriendlyApiErrorMessage(error, 'No pudimos actualizar el asistente. Inténtalo nuevamente.'),
        type: 'error'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (authLoading) {
    return (
      <div className="new-assistant-container">
        <div className="loading-container">
          <LoadingSpinner message="Cargando asistente..." />
        </div>
      </div>
    );
  }

  const renderContent = () => {
    if (!isProfesor) {
      return (
        <div className="content-card">
          <div className="no-permission-container">
            <div className="no-permission-icon">
              <i className="fas fa-lock"></i>
            </div>
            <h2>Acceso restringido</h2>
            <p>Esta sección está disponible únicamente para usuarios con rol Profesor.</p>
            <button className="btn btn-primary" onClick={handleGoBack}>
              <i className="fas fa-arrow-left"></i>
              Volver a Mis Asistentes
            </button>
          </div>
        </div>
      );
    }

    if (isLoading) {
      return (
        <div className="loading-container">
          <LoadingSpinner message="Cargando asistente..." />
        </div>
      );
    }

    if (!assistantData) {
      return (
        <div className="content-card">
          <div className="no-permission-container">
            <div className="no-permission-icon">
              <i className="fas fa-exclamation-triangle"></i>
            </div>
            <h2>No se encontró el asistente</h2>
            <p>Verifica que el asistente exista o intenta nuevamente más tarde.</p>
            <button className="btn btn-primary" onClick={handleGoBack}>
              <i className="fas fa-arrow-left"></i>
              Volver a Mis Asistentes
            </button>
          </div>
        </div>
      );
    }

    return (
      <div className="new-assistant-content">
        <form className="form-section basic-info-section assistant-edit-form" onSubmit={(e) => e.preventDefault()}>
          <div className="section-header">
            <div className="section-icon">
              <i className="fas fa-robot"></i>
            </div>
            <div className="section-text">
              <h3 className="section-title">Información general</h3>
              <p className="section-description">Actualizal nombre y descripción del asistente</p>
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">
              <i className="fas fa-tag"></i>
              Nombre del asistente
            </label>
            <input
              type="text"
              className="form-input enhanced"
              value={formData.nombre}
              onChange={(e) => setFormData(prev => ({ ...prev, nombre: e.target.value }))}
              placeholder="Nombre del asistente"
            />
          </div>

          <div className="form-group">
            <label className="form-label">
              <i className="fas fa-book"></i>
              Materia asignada *
            </label>
            <select
              className="form-select enhanced"
              value={formData.idMateria}
              onChange={(e) => handleMateriaChange(e.target.value)}
              disabled={isSubmitting || materiasLoading}
            >
              <option value="">Selecciona una materia</option>
              {materias.map((materia) => {
                const id = materia.id || materia.Id;
                const nombre = materia.nombre || materia.Nombre || 'Materia';
                const area = materia.nombreArea || materia.NombreArea;
                return (
                  <option key={id} value={id}>
                    {area ? `${area} · ${nombre}` : nombre}
                  </option>
                );
              })}
            </select>
            {materiasLoading && (
              <div className="input-hint">
                <i className="fas fa-spinner fa-spin"></i>
                Cargando materias asignadas...
              </div>
            )}
            {materiasError && (
              <div className="input-hint" style={{ color: '#dc2626' }}>
                <i className="fas fa-triangle-exclamation"></i>
                {materiasError}
              </div>
            )}
          </div>

          <div className="form-group">
            <label className="form-label">
              <i className="fas fa-link"></i>
              Nickname URL
            </label>
            <input
              type="text"
              className="form-input enhanced"
              value={formData.nicknameUrl}
              onChange={(e) => setFormData(prev => ({ ...prev, nicknameUrl: e.target.value }))}
              placeholder="ej: algebra-lineal-1"
            />
          </div>

          <div className="form-group">
            <label className="form-label">
              <i className="fas fa-align-left"></i>
              Descripción
            </label>
            <textarea
              className="form-input enhanced"
              rows="4"
              value={formData.descripcion}
              onChange={(e) => setFormData(prev => ({ ...prev, descripcion: e.target.value }))}
              placeholder="Descripción corta del asistente"
            />
          </div>

          <div className="form-group">
            <label className="form-label">
              <i className="fas fa-eye"></i>
              Visibilidad y acceso
            </label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1.5rem', alignItems: 'center' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <input
                  type="checkbox"
                  checked={formData.publico}
                  onChange={(e) => {
                    const nextValue = e.target.checked;
                    setFormData(prev => ({
                      ...prev,
                      publico: nextValue,
                      passwordAsistente: nextValue ? '' : prev.passwordAsistente
                    }));
                  }}
                />
                <span>Asistente público</span>
              </label>
            </div>
          </div>

          {!formData.publico && (
            <div className="form-group">
              <label className="form-label">
                <i className="fas fa-key"></i>
                Password del asistente
              </label>
              <input
                type="text"
                className="form-input enhanced"
                value={formData.passwordAsistente}
                onChange={(e) => {
                  setFormData(prev => ({ ...prev, passwordAsistente: e.target.value }));
                }}
                placeholder="Ingresa el password (obligatorio)"
              />
            </div>
          )}

          <div className="form-group">
            <label className="form-label">
              <i className="fas fa-brain"></i>
              Modelo de {providerLabel}
            </label>
            {isRagFlowProvider ? (
              <>
                <select
                  className="form-select enhanced"
                  value={modelName}
                  onChange={(e) => setModelName(e.target.value)}
                  disabled={isSubmitting || ragFlowModelsLoading}
                  required
                >
                  <option value="">
                    {ragFlowModelsLoading ? 'Cargando modelos...' : 'Selecciona un modelo'}
                  </option>
                  {ragFlowModelOptions.map((modelo) => (
                    <option key={modelo.id} value={modelo.id}>
                      {buildRagFlowModelLabel(modelo)}{modelo.esDefault ? ' (default)' : ''}
                    </option>
                  ))}
                </select>
                {ragFlowModelsError && (
                  <div className="input-hint" style={{ color: '#dc2626' }}>
                    <i className="fas fa-triangle-exclamation"></i>
                    {ragFlowModelsError}
                  </div>
                )}
                {selectedRagFlowModelConfig && (
                  <div className="model-characteristics-card">
                    <div className="model-characteristics-header">
                      <i className="fas fa-circle-info"></i>
                      <span>{buildRagFlowModelLabel(selectedRagFlowModelConfig)}</span>
                    </div>
                    {Array.isArray(selectedRagFlowModelConfig.caracteristicas) && selectedRagFlowModelConfig.caracteristicas.length > 0 && (
                      <ul className="model-characteristics-list">
                        {selectedRagFlowModelConfig.caracteristicas.map((caracteristica, index) => (
                          <li key={`${selectedRagFlowModelConfig.id}-${index}`}>{caracteristica}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </>
            ) : (
              <input
                type="text"
                className="form-input enhanced"
                value={modelName || 'Sin modelo'}
                readOnly
              />
            )}
          </div>

          <div className="assistant-summary-grid">
            <div className="summary-item">
              <p className="summary-label">Tipo</p>
              <p className="summary-value">{assistantData.nombreTipoAsistente}</p>
            </div>
            <div className="summary-item">
              <p className="summary-label">Fecha de alta</p>
              <p className="summary-value">
                {assistantData.fechaAlta
                  ? new Date(assistantData.fechaAlta).toLocaleString('es-AR')
                  : '-'}
              </p>
            </div>
            {assistantData.fechaModificacion && (
              <div className="summary-item">
                <p className="summary-label">Última modificación</p>
                <p className="summary-value">
                  {new Date(assistantData.fechaModificacion).toLocaleString('es-AR')}
                </p>
              </div>
            )}
            <div className="summary-item">
              <p className="summary-label">ID {providerLabel}</p>
              <p className="summary-value">{isRagFlowProvider ? (assistantData.idRagFlowChat || assistantData.IdRagFlowChat || assistantData.idOpenAIAssistant) : assistantData.idOpenAIAssistant}</p>
            </div>
          </div>

        </form>

        <div className="form-section files-section">
          <div className="section-header">
            <div className="section-icon">
              <i className="fas fa-file-pdf"></i>
            </div>
            <div className="section-text">
              <h3 className="section-title">Archivos del asistente</h3>
              <p className="section-description">Agrega nuevos PDFs o elimina los existentes</p>
            </div>
          </div>

          <div className="files-management">
            <div className="existing-files-panel">
              <div className="existing-files-header">
                <h4>Archivos actuales ({existingFiles.length})</h4>
                {assistantData.fechaAlta && (
                  <p className="files-date">
                    Fecha de alta: {new Date(assistantData.fechaAlta).toLocaleString('es-AR')}
                  </p>
                )}
              </div>
              {existingFiles.length === 0 ? (
                <p className="no-files-text">No hay archivos cargados todavía.</p>
              ) : (
                <ul className="existing-files-list">
                  {existingFiles.map((file) => (
                    <li key={file.fileId}>
                      <i className="fas fa-file-pdf"></i>
                      <span>{formatFileName(file.fileName)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div
              className={`file-dropzone ${isDragOver ? 'dragover' : ''} ${totalFilesCount > 0 ? 'has-files' : ''}`}
              onDragOver={(e) => {
                e.preventDefault();
                setIsDragOver(true);
              }}
              onDragLeave={(e) => {
                e.preventDefault();
                setIsDragOver(false);
              }}
              onDrop={(e) => {
                e.preventDefault();
                setIsDragOver(false);
                const files = Array.from(e.dataTransfer.files);
                handleAddFiles(files);
              }}
            >
              <div className="dropzone-border">
                <div className="dropzone-content">
                  <div className="dropzone-icon">
                    <i className="fas fa-cloud-upload-alt"></i>
                  </div>
                  <div className="dropzone-text">
                    <h4>Arrastra archivos PDF o selecciona desde tu equipo</h4>
                    <p>Los archivos nuevos se sumarán a los existentes</p>
                  </div>
                  <div className="dropzone-divider">
                    <span>o</span>
                  </div>
                  <label className="file-input-label">
                    <input
                      type="file"
                      multiple
                      accept=".pdf"
                      onChange={(e) => {
                        const files = Array.from(e.target.files);
                        handleAddFiles(files);
                        e.target.value = '';
                      }}
                      style={{ display: 'none' }}
                    />
                    <span className="btn btn-upload">
                      <i className="fas fa-folder-open"></i>
                      Seleccionar Archivos PDF
                    </span>
                  </label>
                  <div className="upload-limits">
                    <div className="limit-item">
                      <i className="fas fa-file-alt"></i>
                      <span>Máximo 20 archivos</span>
                    </div>
                    <div className="limit-item">
                      <i className="fas fa-file-pdf"></i>
                      <span>Solo formato PDF</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {(existingFiles.length > 0 || newFiles.length > 0) && (
            <div className="selected-files">
              <h4>
                Total de archivos ({totalFilesCount}/20)
              </h4>

              {newFiles.length > 0 && (
                <>
                  <h5>Nuevos archivos seleccionados</h5>
                  <div className="files-list">
                    {newFiles.map((file, index) => (
                      <div key={`${file.name}-${index}`} className="file-item">
                        <div className="file-info">
                          <i className="fas fa-file-pdf"></i>
                          <span className="file-name">{formatFileName(file.name)}</span>
                          <span className="file-size">
                            {(file.size / 1024 / 1024).toFixed(2)} MB
                          </span>
                        </div>
                        <button
                          type="button"
                          className="remove-file-btn"
                          title="Eliminar archivo"
                          onClick={() => handleRemoveNewFile(index)}
                        >
                          <i className="fas fa-times"></i>
                        </button>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {shouldShowPromptSettings && (
          <div className="form-section prompt-settings-section">
            <div className="section-header">
              <div className="section-icon">
                <i className="fas fa-scroll"></i>
              </div>
              <div className="section-text">
                <h3 className="section-title">Instrucciones y aclaraciones</h3>
                <p className="section-description">Define el comportamiento textual del asistente después de revisar sus archivos.</p>
              </div>
            </div>

            {isInstitutionalTipoSeleccionado && (
              <div className="form-group">
                <label className="form-label">
                  <i className="fas fa-scroll"></i>
                  Instrucciones del asistente institucional *
                </label>
                <textarea
                  className="form-input enhanced"
                  rows="6"
                  value={formData.instruccionesInstitucionales}
                  onChange={(e) => setFormData(prev => ({ ...prev, instruccionesInstitucionales: e.target.value }))}
                  placeholder="Describe el comportamiento y las reglas específicas del asistente institucional"
                />
              </div>
            )}

            <div className="form-group">
              <label className="form-label">
                <i className="fas fa-pen"></i>
                Prompt extra (opcional)
              </label>
              <textarea
                className="form-input enhanced"
                rows="5"
                value={formData.promptExtra}
                onChange={(e) => setFormData(prev => ({ ...prev, promptExtra: e.target.value }))}
                placeholder="Aclaraciones adicionales para el asistente"
              />
            </div>
          </div>
        )}

        <div className="assistant-form-actions">
          <button className="btn btn-outline" type="button" onClick={handleGoBack} disabled={isSubmitting}>
            Cancelar
          </button>
          <button className="btn btn-primary" type="button" onClick={handleSaveClick} disabled={isSubmitting}>
            {isSubmitting ? (
              <>
                <i className="fas fa-spinner fa-spin" /> Guardando...
              </>
            ) : (
              'Guardar cambios'
            )}
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="new-dashboard">
      {isSubmitting && (
        <LoadingSpinner message="Actualizando asistente..." />
      )}
      <aside className="dashboard-sidebar">
        <div className="sidebar-header">
          <div className="sidebar-logo">
            <i className="fas fa-graduation-cap"></i>
            <span>UTN FRC</span>
          </div>
        </div>

        <nav className="sidebar-nav">
          <ProfessorMenuOptions
            user={user}
            isGptClassicEnabled={isGptClassicEnabled}
            isOpenAIAssistantEnabled={isOpenAIAssistantEnabled}
            isRagFlowAssistantEnabled={isRagFlowAssistantEnabled}
            extraItems={[isRagFlowProvider ? NEW_RAGFLOW_ASSISTANT_MENU_ITEM : NEW_ASSISTANT_MENU_ITEM]}
            activeItemId={isRagFlowProvider ? 'mis-ragflow-asistentes' : 'mis-asistentes'}
            onSelect={handleProfessorMenuSelect}
          />
        </nav>

        <div className="sidebar-footer">
          <div className="sidebar-user">
            <div className="user-avatar">
              <i className="fas fa-user"></i>
            </div>
            <div className="user-info">
              <span className="user-name">{user?.displayName || user?.name || user?.email || 'Usuario'}</span>
              <span className="user-role">{user?.rol || user?.role || 'Profesor'}</span>
            </div>
          </div>
        </div>
      </aside>

      <main className="dashboard-main">
        <header className="dashboard-header">
          <div className="header-left">
            <h1 className="page-title">
              <i className="fas fa-pen-square"></i>
              Editar asistente {providerLabel}
            </h1>
            <p className="page-subtitle">
              {assistantData?.nombre
                ? `Modificando "${assistantData.nombre}"`
                : 'Actualiza la información y archivos del asistente'}
            </p>
          </div>

          <div className="header-right">
            <ActiveRegionalBadge />
            <ProcesosMenu />
            <button className="btn btn-secondary" onClick={handleGoBack}>
              <i className="fas fa-arrow-left"></i>
              Volver
            </button>
            <button className="logout-btn-header" onClick={handleLogout} title="Cerrar Sesión">
              <i className="fas fa-sign-out-alt"></i>
              <span className="logout-text">Salir</span>
            </button>

            <div className="user-menu">
              <button
                className="user-menu-trigger"
                onClick={() => setShowUserMenu(!showUserMenu)}
              >
                <div className="user-avatar">
                  <i className="fas fa-user"></i>
                </div>
                <i className="fas fa-chevron-down"></i>
              </button>

              {showUserMenu && (
                <div className="user-menu-dropdown">
                  <div className="dropdown-header">
                    <span className="user-name">{user?.displayName || user?.name || user?.email || 'Usuario'}</span>
                    <span className="user-email">{user?.email || 'usuario@utn.edu.ar'}</span>
                  </div>
                  <div className="dropdown-divider"></div>
                  <button className="dropdown-item logout-item" onClick={handleLogout}>
                    <i className="fas fa-sign-out-alt"></i>
                    Cerrar Sesión
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        <div className="dashboard-content">
          {alert.show && (
            <Alert
              title={alert.title}
              message={alert.message}
              type={alert.type}
              onClose={() => setAlert(prev => ({ ...prev, show: false }))}
            />
          )}

          {renderContent()}
        </div>
      </main>
      {showConfirmation && (
        <div className="confirm-modal-overlay">
          <div className="confirm-modal">
            <h3>Reemplazar archivos del asistente</h3>
            <p>Se actualizará el asistente, reemplazando todos sus archivos actuales por los nuevos que acabas de adjuntar.</p>
            <div className="confirm-modal-actions">
              <button className="btn btn-outline" type="button" onClick={() => setShowConfirmation(false)}>
                Cancelar
              </button>
              <button className="btn btn-primary" type="button" onClick={() => { setShowConfirmation(false); submitUpdate(); }}>
                Entiendo, actualizar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EditAssistant;
